import { ArrowRight, Newspaper, Calendar, Clock, Search, TrendingUp, Heart } from "lucide-react"
import { Button } from "../../components/ui/button"
import { Helmet } from "react-helmet-async"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { loadPublicPosts } from "../../store/slices/publicSlice"
import { getCategoryLabel } from "../../utils/categoryLabels"

const BLOG_CATEGORIES = ["Tous", "blogOfStudents", "blogOfSpaceOfStudents"]
const BLOG_LIMIT = 12

function estimateReadTime(html) {
  const text = html?.replace(/<[^>]+>/g, "") || ""
  const words = text.split(/\s+/).filter(Boolean).length
  return `${Math.max(1, Math.ceil(words / 200))} min`
}

export default function Blog() {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { posts: rawPosts, totalPosts, loading } = useSelector((s) => s.public)
    const [selectedCategory, setSelectedCategory] = useState("Tous")
    const [searchQuery, setSearchQuery] = useState("")
    const [page, setPage] = useState(1)

    useEffect(() => {
        const params = { limit: BLOG_LIMIT, page, sort: "recent" }
        params.category = "blogOfStudents"
        if (selectedCategory !== "Tous" && selectedCategory !== "blogOfStudents") params.category = selectedCategory
        if (searchQuery.trim()) params.search = searchQuery.trim()
        dispatch(loadPublicPosts(params))
    }, [dispatch, selectedCategory, searchQuery, page])

    useEffect(() => {
        setPage(1)
    }, [selectedCategory, searchQuery])

    const posts = rawPosts
    const categories = BLOG_CATEGORIES
    const filteredPosts = posts

    const stats = [
        { val: "5000+", label: "Lecteurs Hebdo" },
        { val: "12", label: "Contributeurs" },
        { val: "DAILY", label: "Fréquence Pub" },
        { val: "100%", label: "Verified" }
    ]

    return (
        <div className="min-h-screen bg-background font-sans text-foreground">
            <Helmet>
                <title>Le Blog Étudiant - Université de Ngozi</title>
                <meta name="description" content="Restez informé des dernières actualités, événements et conseils pour réussir votre parcours universitaire à l'Université de Ngozi." />
            </Helmet>

            {/* Hero Section - Standard (ref: DroitEcoGestion) */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">La Gazette de l'Université</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Édition Numérique</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">L'Art de</span>
                            <span className="block text-white mt-2">S'Informer</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                Chroniques, actualités et réflexions sur la vie académique à Ngozi.
                                <span className="italic block mt-1 tracking-tight text-accent/80 font-medium">Votre voix, notre communauté.</span>
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <div className="relative w-full sm:w-80">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                <input
                                    type="text"
                                    placeholder="Rechercher un article..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 text-white rounded-xl py-3 pl-11 pr-4 focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all text-sm"
                                />
                            </div>
                            <Button className="bg-accent text-accent-foreground px-8 py-3 text-sm font-black rounded-xl hover:brightness-110 active:scale-95 transition-all w-full sm:w-auto">
                                S'abonner
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            {/* Stats Bar - Newspaper Style */}
            <div className="border-y border-border bg-secondary/5">
                <div className="container py-6 flex flex-wrap items-center gap-12 lg:gap-20">
                    {stats.map((stat, i) => (
                        <div key={i} className="flex items-center gap-3 group">
                            <span className="text-2xl font-black text-primary group-hover:text-accent transition-colors">{stat.val}</span>
                            <span className="text-[10px] text-muted-foreground uppercase font-black tracking-[0.2em]">{stat.label}</span>
                            {i < stats.length - 1 && <span className="hidden lg:block h-4 w-px bg-border ml-4" />}
                        </div>
                    ))}
                </div>
            </div>

            {/* Main Magazine Layout */}
            <article className="container py-16 lg:py-24">
                <div className="grid lg:grid-cols-[280px_1fr] gap-16 lg:gap-24">
                    {/* Sidebar - Sommaire & Info */}
                    <aside className="lg:sticky lg:top-8 lg:self-start hidden lg:block">
                        <div className="space-y-12">
                            <nav className="space-y-2">
                                <p className="text-[10px] uppercase tracking-[0.3em] font-black text-muted-foreground mb-6 pb-2 border-b-2 border-primary/10">
                                    Rubriques
                                </p>
                                {categories.map((cat, i) => (
                                    <button
                                        key={cat}
                                        onClick={() => setSelectedCategory(cat)}
                                        className={`group flex items-center justify-between w-full py-2.5 text-xs font-black transition-all border-r-2 ${selectedCategory === cat ? "text-primary border-primary pr-4" : "text-muted-foreground border-transparent hover:text-primary hover:border-primary/50"}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className={`text-[10px] font-serif italic ${selectedCategory === cat ? "text-accent" : "text-muted-foreground/40"}`}>0{i + 1}</span>
                                            <span className="uppercase tracking-widest">{cat === "Tous" ? cat : getCategoryLabel(cat)}</span>
                                        </div>
                                        <TrendingUp className={`w-3 h-3 ${selectedCategory === cat ? "opacity-100" : "opacity-0"} transition-opacity`} />
                                    </button>
                                ))}
                            </nav>

                            <div className="bg-primary p-8 rounded-3xl text-primary-foreground relative overflow-hidden shadow-2xl">
                                <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -mr-12 -mt-12" />
                                <h4 className="text-[10px] uppercase tracking-[0.2em] font-black mb-6 text-accent">En Bref</h4>
                                <ul className="space-y-4 text-[11px] font-medium border-t border-white/10 pt-4">
                                    <li className="flex justify-between items-center">
                                        <span className="opacity-60">Articles Total</span>
                                        <span className="font-black text-accent">{totalPosts ?? posts.length}</span>
                                    </li>
                                    <li className="flex justify-between items-center">
                                        <span className="opacity-60">Temps de lecture</span>
                                        <span className="font-black text-accent">~5 min</span>
                                    </li>
                                    <li className="flex justify-between items-center">
                                        <span className="opacity-60">Catégories</span>
                                        <span className="font-black text-accent">{categories.length - 1}</span>
                                    </li>
                                </ul>
                                <Button className="mt-8 w-full bg-white/10 hover:bg-white/20 text-white text-[10px] font-black py-4 rounded-xl border border-white/20 uppercase tracking-widest">
                                    Archive 2023
                                </Button>
                            </div>

                            <div className="px-4 py-8 border-2 border-dashed border-border rounded-3xl text-center">
                                <Heart className="w-8 h-8 text-accent mx-auto mb-4" />
                                <h5 className="text-xs font-black uppercase tracking-widest mb-2">Soutenez-nous</h5>
                                <p className="text-[10px] text-muted-foreground font-medium leading-relaxed">
                                    Participez à la rédaction du blog en soumettant vos articles.
                                </p>
                            </div>
                        </div>
                    </aside>

                    {/* Main Content Body */}
                    <main className="space-y-12">
                        {/* Selected Hub Header */}
                        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-border">
                            <div className="space-y-2">
                                <div className="flex items-center gap-3 text-accent transition-all animate-in slide-in-from-left duration-700">
                                    <div className="w-12 h-1 bg-accent rounded-full" />
                                    <span className="text-[10px] font-black uppercase tracking-[0.2em]">Flux d'Actualités</span>
                                </div>
                                <h2 className="text-4xl font-serif font-black italic text-foreground tracking-tight">
                                    {selectedCategory === "Tous" ? "Toutes les Chroniques" : `Focus sur ${getCategoryLabel(selectedCategory)}`}
                                </h2>
                            </div>
                            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground bg-secondary/20 px-4 py-2 rounded-full border border-border">
                                {filteredPosts.length} Articles Trouvés
                            </div>
                        </div>

                        {/* Blog Grid - Adapted Ipl Style Items */}
                        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-16">
                            {(loading && filteredPosts.length === 0 ? [] : filteredPosts).map((post) => {
                                const authorName = post.author?.fullname || "Auteur"
                                const imgUrl = post.headlineImage?.url ?? post.headlineImage ?? ""
                                const readTime = post.readTime || estimateReadTime(post.body)
                                return (
                                <article
                                    key={post._id}
                                    className="group flex flex-col cursor-pointer"
                                    onClick={() => navigate(`/article/${post._id}`)}
                                >
                                    <div className="relative aspect-[4/3] mb-6 overflow-hidden rounded-2xl bg-slate-100 border border-border transition-all duration-500 group-hover:-translate-y-2 group-hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)]">
                                        {imgUrl && <img src={imgUrl} alt={post.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />}
                                        <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm scale-90 opacity-0 group-hover:opacity-100 group-hover:scale-100 transition-all duration-500">
                                            <span className="text-[10px] font-black text-primary uppercase tracking-widest">{getCategoryLabel(post.category)}</span>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60">
                                            <span className="flex items-center gap-1.5 group-hover:text-accent transition-colors">
                                                <Calendar className="w-3 h-3" />
                                                {new Date(post.publishedAt || post.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                                            </span>
                                            <span className="w-1 h-1 bg-border rounded-full" />
                                            <span className="flex items-center gap-1.5">
                                                <Clock className="w-3 h-3" />
                                                {readTime}
                                            </span>
                                        </div>

                                        <h3 className="text-2xl font-serif font-bold group-hover:text-primary transition-colors leading-tight line-clamp-2">
                                            {post.title}
                                        </h3>

                                        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 font-medium opacity-80 group-hover:opacity-100 transition-opacity">
                                            {post.excerpt}
                                        </p>

                                        <div className="pt-4 flex items-center justify-between border-t border-border/50">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-accent text-accent-foreground font-black text-[10px] flex items-center justify-center border-2 border-white/20 shadow-sm">
                                                    {authorName.charAt(0)}
                                                </div>
                                                <span className="text-[11px] font-black uppercase tracking-widest text-primary">{authorName}</span>
                                            </div>
                                            <div className="w-10 h-10 rounded-xl bg-secondary/20 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-all duration-300">
                                                <ArrowRight className="w-5 h-5" />
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            )})}
                        </div>

                        {!loading && filteredPosts.length === 0 && (
                            <div className="text-center py-20 bg-secondary/10 rounded-3xl border-2 border-dashed border-border opacity-50">
                                <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                <h3 className="text-xl font-bold">Aucun article trouvé</h3>
                                <p className="text-sm">Nous n'avons trouvé aucun article correspondant à "{searchQuery}" dans cette rubrique.</p>
                            </div>
                        )}

                        {totalPosts > BLOG_LIMIT && (
                            <div className="flex items-center justify-center gap-4 py-8 mt-8">
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page <= 1}
                                    className="px-4 py-2 rounded-xl border border-border bg-card text-sm font-bold text-foreground hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Précédent
                                </button>
                                <span className="text-sm text-muted-foreground">
                                    Page {page} sur {Math.ceil(totalPosts / BLOG_LIMIT) || 1}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => p + 1)}
                                    disabled={page * BLOG_LIMIT >= totalPosts}
                                    className="px-4 py-2 rounded-xl border border-border bg-card text-sm font-bold text-foreground hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Suivant
                                </button>
                            </div>
                        )}
                    </main>
                </div>
            </article>

            {/* Newsletter Subscription - Ipl Footer Style */}
            <footer className="bg-primary text-white border-t-8 border-accent relative overflow-hidden">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-accent/5 -skew-x-12 origin-top-right pointer-events-none" />
                <div className="container py-24 text-center relative z-10">
                    <div className="max-w-xl mx-auto space-y-8">
                        <span className="text-[10px] uppercase tracking-[0.4em] text-accent font-black block mb-4">Newsletter Hebdomadaire</span>
                        <h2 className="text-4xl lg:text-5xl font-serif italic mb-8 leading-tight">
                            Recevez le meilleur de <br /><span className="text-accent underline decoration-accent/30 decoration-8 underline-offset-4">Ngozi Pulse</span>.
                        </h2>
                        <div className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto bg-white/5 p-2 rounded-2xl border border-white/10 backdrop-blur-sm">
                            <input
                                type="email"
                                placeholder="votre@email.com"
                                className="flex-1 px-6 py-4 rounded-xl bg-transparent text-white focus:outline-none placeholder:text-slate-500 text-sm font-medium"
                            />
                            <button className="px-10 py-4 bg-accent hover:bg-white hover:text-accent text-white font-black rounded-xl transition-all duration-300 shadow-2xl shadow-accent/20">
                                S'abonner
                            </button>
                        </div>
                        <p className="text-[10px] text-white/40 uppercase tracking-widest font-black pt-8">
                            © 2024 Université de Ngozi · Gazette Estudiantine
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    )
}
